import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LocalStorageService } from "src/app/utils/localstorage.service";
import { BehaviorSubject, Subject } from "rxjs";
import { environment } from "../../environments/environment";

export type Data = {
  id: number;
  user: {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    user_hash: string;
    pictureUrl: string;
  };
  company: number;
  user_type: number;
  access_level: number;
  created: string;
  pin: string;
  pin_timeout: number;
  subscription: number;
  has_web_push_enabled: boolean;
  onesignal_userid: string;
  onesignal_token: string;
  clients: any;
  has_pin: boolean;
  stripe_customer_id: string;
  error_status: any;
  phone_number: string;
  uuid: string;
  is_2fa_enabled: boolean;
  subscription_active: boolean;
  tier: string;
  stripe_subscription_status: string;
  active: boolean;
};

export type Company = {
  accounting_company: any;
  id: number;
  uuid: string;
  name: string;
  logo: string;
  url: string;
  twitter: string;
  facebook: string;
  linkedin: string;
  owner: number;
  is_accounting: boolean;
  color: string;
  review_proadvisor: string;
  review_xero: string;
  review_yelp: string;
  review_google: string;
  review_linkedin: string;
  display_name: boolean;
  archived: boolean;
  last_used: string;
  notes: string;
  notes_last_updated: string;
  has_magic_answers?: boolean;
  is_indexing_magic_answers_data?: boolean;
  magic_answers_enabled?: boolean;
};

export type User = {
  id?: number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  user_hash?: string;
  pictureUrl?: string;
};

export enum SubscriptionType {
  FREE = "free",
  PAID = "paid",
  PREMIUM = "premium",
}

export enum UserType {
  ACCOUNTANT = 1,
  CLIENT = 2,
}

export enum SubscriptionLabel {
  FREE_ACCOUNTANT = "FREE_ACCOUNTANT",
  FREE_CLIENT = "FREE_CLIENT",
  PAID_ACCOUNTANT = "PAID_ACCOUNTANT",
  PAID_CLIENT = "PAID_CLIENT",
}

export enum SubscriptionErrorStatus {
  SUBSCRIPTION_PAST_DUE = "past_due",
  SUBSCRIPTION_CANCELED = "canceled",
  SUBSCRIPTION_PAYMENT_METHOD_EXPIRING = "payment_method_expiring",
  SUBSCRIPTION_TRIAL_ENDING = "trial_ending",
  SUBSCRIPTION_PAYMENT_FAILED = "payment_failed",
}

@Injectable({ providedIn: "root" })
export class SharedDataService {
  _data: Data;
  _subscriptionType: string;

  company: Company;
  user: User;

  _subscriptionLabel: SubscriptionLabel;
  _usertypeLabel: UserType;

  $dataChanged = new Subject<any>();

  constructor(
    private localStorage: LocalStorageService,
    private http: HttpClient,
  ) {
    this.data = this.localStorage.get("data");
    this.subscriptionType = this.localStorage.get("subscription_type");
    this.company = this.localStorage.get("company");
    this.user = this.localStorage.get("user");

    if (this.data != null) {
      if (this.data.error_status == null) {
        this.data.error_status = [];
      }
    }

    this.getSubscriptionType().then(
      (res) => {
        this.subscriptionType = res.subscription_type;
      },
      (err) => {
        this.subscriptionType = null;
      },
    );
  }

  public set subscriptionType(subscriptionType: string) {
    let lastSubscriptionType = this._subscriptionType;

    this._subscriptionType = subscriptionType;
    this.localStorage.set("subscription_type", subscriptionType);

    if (
      lastSubscriptionType != null &&
      lastSubscriptionType != subscriptionType
    ) {
      // refresh the page
      window.location.reload();
    }
  }

  public get subscriptionType(): string {
    return this._subscriptionType;
  }

  public set data(data: Data) {
    this._data = data;
    this.localStorage.set("data", data);

    if (this._data != null) {
      if (this._data.error_status == null) {
        this._data.error_status = [];
      }
    }

    this.$dataChanged.next(null);
  }

  public get data(): Data {
    return this._data;
  }

  public get subscriptionLabel(): SubscriptionLabel {
    return this.getSubscriptionLabel();
  }

  public get usertypeLabel(): UserType {
    return this.getUsertypeLabel();
  }

  getSubscriptionLabel() {
    if (this.subscriptionType === "free") {
      if (this._data.user_type === 1) return SubscriptionLabel.FREE_ACCOUNTANT;

      return SubscriptionLabel.FREE_CLIENT;
    }
    if (this.subscriptionType === "paid") {
      if (this._data.user_type === 1) return SubscriptionLabel.PAID_ACCOUNTANT;
    }
    return SubscriptionLabel.PAID_CLIENT;
  }

  getUsertypeLabel() {
    if (this._data.user_type === 1) return UserType.ACCOUNTANT;
    return UserType.CLIENT;
  }

  getSubscriptionType(): Promise<any> {
    return this.http
      .get(`${environment.apiUrl}/user/subscription-type`)
      .toPromise();
  }

  hasErrorStatus(errorStatus: any): boolean {
    if (this._data.error_status == null) return false;
    return this._data.error_status.includes(errorStatus);
  }
}
